import { isArray, mean } from 'lodash';

import { CONTAINER_SIZE, MODE_WATER, RATES_CONTRACT_LENGTH } from '@/lib/constants';
import { MarketRateModel, MarketRateQueryModel } from '@/lib/models/rates/types';
import { CargoWeightValue, ContainerSizeType } from '@/types';
import {
  SegmentModeType,
  XenetaContractLengthApiType,
  XenetaRatesFieldApiType,
  XenetaWeightBracketApiType,
} from '@/types/api-types';
import { ChartDatum } from '@/types/charts';

import { getContainerGeneralUnit } from '../cargo';

export const getXenetaAdditionalFields = ({
  containerSize,
  cargoWeight,
  contractLength,
  mode,
}: {
  containerSize?: ContainerSizeType[];
  cargoWeight?: CargoWeightValue;
  contractLength?: XenetaContractLengthApiType[];
  mode: SegmentModeType;
}): XenetaRatesFieldApiType | undefined => {
  // @TODO expand this capability in the future based on user params
  return mode === MODE_WATER
    ? {
        containerTypes: containerSize,
        contractLength: contractLength || [RATES_CONTRACT_LENGTH.SHORT],
        contractedWithin: ['all'],
      }
    : {
        weightBracket: [getXenetaAirWeightBracket(cargoWeight)],
        contractLength: contractLength || [RATES_CONTRACT_LENGTH.LONG],
      };
};

export const extractXenetaAdditionalFields = (additionalFields?: XenetaRatesFieldApiType) => {
  if (!additionalFields) return {};
  let containerTypes: ContainerSizeType[] | undefined;
  let contractLength: XenetaContractLengthApiType[] | undefined;
  let weightBracket: XenetaWeightBracketApiType[] | undefined;

  if ('weightBracket' in additionalFields && additionalFields?.weightBracket?.length) {
    weightBracket = additionalFields.weightBracket;
  }

  if ('contractLength' in additionalFields && additionalFields?.contractLength?.length) {
    contractLength = additionalFields.contractLength;
  }

  if ('containerTypes' in additionalFields && additionalFields?.containerTypes?.length) {
    containerTypes = additionalFields.containerTypes;
  }

  return { containerTypes, contractLength, weightBracket };
};

export const getXenetaAirWeightBracket = (cargoWeight?: CargoWeightValue): XenetaWeightBracketApiType => {
  if (cargoWeight) {
    // Assuming KG for the moment
    if (cargoWeight.value > 1000) {
      return 'greaterThan1000kg';
    }
    if (cargoWeight.value > 500) {
      return 'greaterThan500kg';
    }
    if (cargoWeight.value > 200) {
      return 'greaterThan200kg';
    }
    if (cargoWeight.value > 45) {
      return 'greaterThan45kg';
    }
    if (cargoWeight.value <= 45) {
      return 'lessThan45kg';
    }
  }

  // Return default
  return 'greaterThan45kg';
};

export const xenetaAirWeightBracketToString = (cargoWeights?: string[]): string => {
  if (cargoWeights?.length) {
    const weight = cargoWeights[0];
    if (weight === 'greaterThan1000kg') return '1000+kg';
    if (weight === 'greaterThan500kg') return '500-1000kg';
    if (weight === 'greaterThan200kg') return '200-500kg';
    if (weight === 'greaterThan45kg') return '45-200kg';
    if (weight === 'lessThan45kg') return '0-45kg';
  }
  return '';
};

export const xenetaCargoSizeBracketToString = (containerSizes?: string[]): string => {
  if (containerSizes?.length) {
    const size = containerSizes[0];
    if (size === CONTAINER_SIZE.FORTY) return '40ft';
    if (size === CONTAINER_SIZE.TWENTY) return '20ft';
  }
  return '';
};

export const xenetaContractLengthToString = (queryContractLength?: string[]): string => {
  if (queryContractLength?.length) {
    const length = queryContractLength[0];
    if (length === RATES_CONTRACT_LENGTH.SHORT) return 'Spot';
    if (length === RATES_CONTRACT_LENGTH.LONG) return 'Contract';
  }
  return '';
};

export const createMarketRatesKey = ({
  ports,
  additionalFields,
}: Pick<MarketRateQueryModel, 'ports' | 'additionalFields'>) => {
  const { pol, pod } = ports[0] || {};
  const { containerTypes, contractLength } = extractXenetaAdditionalFields(additionalFields);
  const additionalFieldsKey = `${(containerTypes || []).join(',')}_${(contractLength || []).join(',')}`;

  return {
    fullKey: `${getFirstPartOfPortCode(pol)}_${getFirstPartOfPortCode(pod)}_${additionalFieldsKey}`,
    additionalFieldsKey,
  };
};

const getFirstPartOfPortCode = (identifier?: string): string => {
  const parts = identifier?.split('|');
  return parts?.[0] || identifier || '';
};

const getChartDomainBuffer = (val: number): number => {
  if (val < 10) return 5;
  if (val < 100) return 10;
  if (val < 500) return 100;
  if (val < 1000) return 200;
  if (val < 10000) return 500;
  return 1000;
};

export const calcRatesChartRange = (data?: ChartDatum[]) => {
  // Find the min and max values from the data and add buffers to them
  const values = (data || []).map((point) => point.value) as number[];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const average = mean(values);
  const roundedAverage = average > 100 ? Math.round(average) : average;
  const buffer = getChartDomainBuffer(average);

  return { min, max, average: roundedAverage, domain: [Math.round(min - buffer), Math.round(max + buffer)] };
};

export const convertRatesToChartData = (rates?: MarketRateModel): ChartDatum[] => {
  if (!rates || !rates.data) return [];

  const dataObject = rates.data;
  const seriesKey = Object.keys(dataObject)[0];
  const { data: nestedData } = dataObject[seriesKey];

  return Object.keys(nestedData).map((rowKey) => {
    const row = nestedData[rowKey];
    if (isArray(row)) {
      return { date: rowKey, value: row[0] };
    }
    return { date: rowKey, value: row.value };
  });
};

export const getRatesSeriesLabel = (rates?: MarketRateModel) => {
  if (!rates?.data) return undefined;

  const dataObject = rates.data;
  const seriesKey = Object.keys(dataObject)[0];
  const { seriesLabel } = dataObject[seriesKey];

  // Long Months6 DC20
  // Long All GreaterThan500kg
  const seriesParts = (seriesLabel || '').split(' ');
  const [range, contract, size] = seriesParts;

  return {
    range,
    contract,
    size: getContainerGeneralUnit(size),
  };
};
